// src/pages/stock/EditStockCode.tsx
//
// Edit Stock Code
//
// Page version of the legacy "EditStockCodeModalContent" (Modal -> Page).
// This screen edits an existing Stock Code entity loaded by :stockId.
//
// Header policy
// - Title/description/back are defined in route handles (App.tsx).
// - This page does not inject header actions.
// - This page *does* update the header leading slot to show the Stock Code and a small status hint:
//   - "● Modified" while the form is dirty
//   - "Saved" briefly after a successful save
// - Header slots are cleared on unmount to avoid leaking state to other pages.
//
// UX / behavior
// - Compact, left-aligned layout (maxWidth=900).
// - Fully MUI-based UI.
// - Region/Supplier use Autocomplete with text filtering (supplier list sorted by label).
// - Save is enabled only when the form is valid, dirty, and not busy.
// - Cancel is guarded by a discard confirmation when dirty.
// - Delete is separated into a Danger Zone section and requires confirmation.
// - Uses shared confirm modal + systemAlert for feedback (no window.alert/confirm).
//
// Route: /stocks/codes/:stockId/edit

import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { EsimType, StockStatus } from '@models/stock';
import { StockSupplierTaskType, type StockUpsertRequest, editStock, getStock } from '@apis/stock';

import { useRegionList } from '@queries/regions/useRegionList';
import { useAllSupplierList } from '@queries/suppliers/useAllSupplierList';

import { ESIM_TYPE_OPTION_LIST } from '@constants/stocks';
import { dropEmptyValues } from '@utils/common';

import { usePageHeader } from '@components/common/pageHeader.context';
import DangerZoneCard from '@components/common/DangerZoneCard';
import { useConfirm } from '@components/common/confirm/confirm.context';
import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';

type Option<T> = { label: string; value: T };

const PAGE_MAX_WIDTH = 900;
const ACTION_MIN_WIDTH = 140;
const SAVED_HINT_MS = 1200;

const normalize = (v?: string | null) => (v ?? '').trim();

/**
 * Normalize only fields that matter for dirty checks.
 * - Prevents false dirty states due to whitespace differences.
 * - Ensures topUpValidityDays is only comparable when API integration is enabled.
 */
const toComparable = (f: StockUpsertRequest) => ({
  stockCode: normalize(f.stockCode),
  stockName: normalize(f.stockName),
  regionId: Number(f.regionId) || 0,
  supplierId: Number(f.supplierId) || 0,
  isUsingApi: !!f.isUsingApi,
  status: f.status,
  esimType: f.esimType,
  topUpValidityDays: f.isUsingApi ? (f.topUpValidityDays ?? undefined) : undefined,
});

/** Lightweight deep compare for small objects (keeps dependencies minimal). */
const shallowJsonEqual = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

const EditStockCode = () => {
  const navigate = useNavigate();
  const { stockId } = useParams();
  const stockIdNumber = React.useMemo(() => Number(stockId), [stockId]);

  const { setPageHeaderSlots } = usePageHeader();
  const confirm = useConfirm();
  const systemAlert = useSystemAlert();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // --------------------
  // Loading state
  // --------------------
  const [isLoading, setIsLoading] = React.useState(true);
  const [loadingError, setLoadingError] = React.useState<string | null>(null);

  // Keep a stable display for the header even if the form changes.
  const [originalStockCode, setOriginalStockCode] = React.useState('');

  // --------------------
  // Form state
  // --------------------
  const [form, setForm] = React.useState<StockUpsertRequest>({
    stockCode: '',
    stockName: '',
    regionId: 0,
    supplierId: 0,
    isUsingApi: false,
    status: StockStatus.ACTIVE,
    esimType: EsimType.LPA,
    topUpValidityDays: undefined,
  });

  // Baseline snapshot for dirty detection (set after initial load).
  const baselineRef = React.useRef<ReturnType<typeof toComparable> | null>(null);

  // --------------------
  // Mutations
  // --------------------
  const [isSaving, setIsSaving] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const isBusy = isLoading || isSaving || isDeleting;

  // --------------------
  // Saved hint (header)
  // --------------------
  const [savedHintUntil, setSavedHintUntil] = React.useState<number | null>(null);

  const showSavedHint = React.useMemo(() => {
    return savedHintUntil !== null && Date.now() < savedHintUntil;
  }, [savedHintUntil]);

  React.useEffect(() => {
    if (!savedHintUntil) return;

    const remaining = savedHintUntil - Date.now();
    if (remaining <= 0) {
      setSavedHintUntil(null);
      return;
    }

    const t = window.setTimeout(() => setSavedHintUntil(null), remaining);
    return () => window.clearTimeout(t);
  }, [savedHintUntil]);

  // --------------------
  // Options (Region / Supplier)
  // --------------------
  const { data: regionsData, isLoading: isRegionsLoading } = useRegionList();
  const { data: suppliersData, isLoading: isSuppliersLoading } = useAllSupplierList();

  const regionOptions = React.useMemo<Array<Option<number>>>(() => {
    return (regionsData?.data ?? []).map((r) => ({ label: r.regionCode, value: r.regionId }));
  }, [regionsData]);

  const supplierOptions = React.useMemo<Array<Option<number>>>(() => {
    const list = (suppliersData?.data ?? []).map((s) => ({
      label: `${s.supplierName} (${s.supplierCode})`,
      value: s.supplierId,
    }));
    return list.sort((a, b) => a.label.localeCompare(b.label));
  }, [suppliersData]);

  const selectedRegion = React.useMemo(() => {
    if (!form.regionId) return null;
    return regionOptions.find((o) => o.value === form.regionId) ?? null;
  }, [form.regionId, regionOptions]);

  const selectedSupplier = React.useMemo(() => {
    if (!form.supplierId) return null;
    return supplierOptions.find((o) => o.value === form.supplierId) ?? null;
  }, [form.supplierId, supplierOptions]);

  // --------------------
  // Initial load
  // --------------------
  React.useEffect(() => {
    if (!Number.isFinite(stockIdNumber) || stockIdNumber <= 0) {
      setIsLoading(false);
      setLoadingError('Invalid stockId.');
      return;
    }

    let alive = true;

    (async () => {
      setIsLoading(true);
      setLoadingError(null);

      try {
        const res = await getStock(stockIdNumber);
        const row = res?.data?.[0];

        if (!row) throw new Error('Stock not found.');
        if (!alive) return;

        setOriginalStockCode(row.stockCode);

        const nextForm: StockUpsertRequest = {
          stockCode: row.stockCode,
          stockName: row.stockName,
          regionId: row.region.regionId,
          supplierId: row.supplier.supplierId,
          isUsingApi: row.supplierTaskType === StockSupplierTaskType.API,
          status: row.status,
          esimType: row.esimType,
          topUpValidityDays: row.topUpValidityDays,
        };

        setForm(nextForm);
        baselineRef.current = toComparable(nextForm);
      } catch (e: any) {
        if (!alive) return;
        setLoadingError(e?.message ? String(e.message) : 'Failed to load stock.');
      } finally {
        if (!alive) return;
        setIsLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [stockIdNumber]);

  // --------------------
  // Validation / dirty check
  // --------------------
  const isValid = React.useMemo(() => {
    if (!normalize(form.stockCode)) return false;
    if (!normalize(form.stockName)) return false;
    if (!form.regionId) return false;
    if (!form.supplierId) return false;
    if (!form.esimType) return false;

    if (form.isUsingApi) {
      if (!form.topUpValidityDays || form.topUpValidityDays <= 0) return false;
    }

    return true;
  }, [form]);

  const isDirty = React.useMemo(() => {
    const base = baselineRef.current;
    if (!base) return false;
    return !shallowJsonEqual(toComparable(form), base);
  }, [form]);

  const canSave = isValid && isDirty && !isBusy && !loadingError;

  const saveDisabledReason = React.useMemo(() => {
    if (isBusy || loadingError) return '';
    if (!isValid) return 'Fill in all required fields.';
    if (!isDirty) return 'No changes to save.';
    return '';
  }, [isBusy, loadingError, isDirty, isValid]);

  // --------------------
  // Header leading slot
  // --------------------
  React.useEffect(() => {
    const displayCode = originalStockCode || form.stockCode || '-';
    const description = isDirty ? '● Modified' : showSavedHint ? 'Saved' : undefined;

    setPageHeaderSlots({
      leading: {
        title: displayCode,
        description,
      },
    });
  }, [form.stockCode, isDirty, originalStockCode, setPageHeaderSlots, showSavedHint]);

  // --------------------
  // Field handlers
  // --------------------
  const handleChangeText =
    (key: 'stockName' | 'topUpValidityDays') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;

      if (key === 'topUpValidityDays') {
        const n = Number(raw);
        setForm((prev) => ({
          ...prev,
          topUpValidityDays: Number.isFinite(n) && n > 0 ? n : undefined,
        }));
        return;
      }

      setForm((prev) => ({ ...prev, [key]: raw }));
    };

  const handleChangeProductType = (e: SelectChangeEvent<unknown>) => {
    const v = e.target.value;
    setForm((prev) => ({ ...prev, esimType: v as any }));
  };

  const handleToggleActive = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setForm((prev) => ({ ...prev, status: checked ? StockStatus.ACTIVE : StockStatus.INACTIVE }));
  };

  const handleToggleCarrierApi = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setForm((prev) => ({
      ...prev,
      isUsingApi: checked,
      // Do not keep an irrelevant value when API integration is disabled.
      topUpValidityDays: checked ? prev.topUpValidityDays : undefined,
    }));
  };

  // --------------------
  // Actions
  // --------------------
  const handleSave = React.useCallback(async () => {
    if (!Number.isFinite(stockIdNumber) || stockIdNumber <= 0) return;
    if (isBusy) return;

    if (!isValid) {
      systemAlert.info('Please fill in all required fields correctly.');
      return;
    }
    if (!isDirty) {
      systemAlert.info('No changes to save.');
      return;
    }

    try {
      setIsSaving(true);

      const payload = dropEmptyValues(form, ['topUpValidityDays']);
      await editStock(stockIdNumber, payload);

      // Update baseline so the form becomes "clean" after a successful save.
      baselineRef.current = toComparable(form);

      setSavedHintUntil(Date.now() + SAVED_HINT_MS);
      systemAlert.success('Stock code updated successfully.');
    } catch {
      systemAlert.error('Failed to update stock code. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [form, isBusy, isDirty, isValid, stockIdNumber, systemAlert]);

  const handleDelete = React.useCallback(async () => {
    if (!Number.isFinite(stockIdNumber) || stockIdNumber <= 0) return;
    if (isBusy) return;

    const ok = await confirm({
      priority: 'danger',
      title: 'Delete Stock Code',
      description: (
        <>
          This action will mark the stock code as deleted. You can’t easily undo this action.
          <br />
          <b>{originalStockCode || form.stockCode}</b>
        </>
      ),
      confirmText: 'Delete',
      cancelText: 'Cancel',
      autoFocusConfirm: false,
    });

    if (!ok) return;

    try {
      setIsDeleting(true);

      await editStock(stockIdNumber, {
        ...form,
        isDeleted: true,
        supplierId: form.supplierId,
      });

      systemAlert.success('Stock code deleted.');
      navigate('/stocks/codes', { replace: true });
    } catch {
      systemAlert.error('Failed to delete stock code. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  }, [confirm, form, isBusy, navigate, originalStockCode, stockIdNumber, systemAlert]);

  const handleCancel = React.useCallback(async () => {
    if (isBusy) return;

    if (!isDirty) {
      navigate(-1);
      return;
    }

    const ok = await confirm({
      priority: 'info',
      title: 'Discard changes?',
      description: 'You have unsaved changes. If you leave now, those changes will be lost.',
      confirmText: 'Discard',
      cancelText: 'Stay',
      autoFocusConfirm: false,
    });

    if (ok) navigate(-1);
  }, [confirm, isBusy, isDirty, navigate]);

  // --------------------
  // Render
  // --------------------
  if (loadingError) {
    return (
      <Box sx={{ py: 2, maxWidth: PAGE_MAX_WIDTH, width: '100%', mr: 'auto' }}>
        <Paper variant="outlined" sx={{ mt: 2, p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Alert severity="error">{loadingError}</Alert>

          <Stack direction="row" spacing={1.25} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={() => navigate(-1)}>
              Back
            </Button>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2, maxWidth: PAGE_MAX_WIDTH, width: '100%', mr: 'auto' }}>
      <Stack spacing={3} sx={{ minWidth: 0 }}>
        <Paper variant="outlined" sx={{ mt: 2, p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
          <Stack spacing={2.5}>
            <TextField
              label="Stock Label"
              value={form.stockName}
              onChange={handleChangeText('stockName')}
              fullWidth
              disabled={isBusy}
              placeholder="Enter the stock label"
            />

            <Autocomplete
              options={regionOptions}
              value={selectedRegion}
              onChange={(_, opt) => setForm((prev) => ({ ...prev, regionId: opt?.value ?? 0 }))}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              loading={isRegionsLoading}
              disabled={isBusy || isRegionsLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Region"
                  placeholder="Type to filter regions"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isRegionsLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <FormControl fullWidth disabled={isBusy}>
              <InputLabel id="product-type-label">Product Type</InputLabel>
              <Select
                labelId="product-type-label"
                label="Product Type"
                value={form.esimType}
                onChange={handleChangeProductType}
              >
                {ESIM_TYPE_OPTION_LIST.map((o) => (
                  <MenuItem key={String(o.value)} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Autocomplete
              options={supplierOptions}
              value={selectedSupplier}
              onChange={(_, opt) => setForm((prev) => ({ ...prev, supplierId: opt?.value ?? 0 }))}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              loading={isSuppliersLoading}
              disabled={isBusy || isSuppliersLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Supplier"
                  placeholder="Type to filter suppliers"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isSuppliersLoading ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            <Divider />

            <Stack spacing={1.25}>
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.status === StockStatus.ACTIVE}
                      onChange={handleToggleActive}
                      disabled={isBusy}
                    />
                  }
                  label="Active"
                />
                <Box sx={{ pl: 5 }}>
                  <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                    Active: sets whether this Stock Code is enabled or disabled.
                  </Alert>
                </Box>
              </Box>

              <Box>
                <FormControlLabel
                  control={
                    <Switch checked={form.isUsingApi} onChange={handleToggleCarrierApi} disabled={isBusy} />
                  }
                  label="Carrier API Integration"
                />
                <Box sx={{ pl: 5 }}>
                  <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                    Carrier API Integration: enables carrier API integration for this Stock Code.
                  </Alert>
                </Box>
              </Box>
            </Stack>

            {form.isUsingApi && (
              <TextField
                label="Top-up Validity (Days)"
                value={form.topUpValidityDays ?? ''}
                onChange={handleChangeText('topUpValidityDays')}
                fullWidth
                disabled={isBusy}
                placeholder="Enter top-up validity in days"
                type="number"
                inputProps={{ min: 1 }}
              />
            )}

            <Divider />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={isBusy}
                startIcon={<CloseOutlinedIcon />}
                fullWidth={isMobile}
                sx={{
                  whiteSpace: 'nowrap',
                  minWidth: isMobile ? undefined : ACTION_MIN_WIDTH,
                }}
              >
                Cancel
              </Button>

              <Tooltip title={saveDisabledReason} disableHoverListener={canSave || !saveDisabledReason}>
                <Box
                  component="span"
                  sx={{
                    display: isMobile ? 'block' : 'inline-block',
                    width: isMobile ? '100%' : 'auto',
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={!canSave}
                    startIcon={isSaving ? <CircularProgress size={18} /> : <SaveOutlinedIcon />}
                    fullWidth={isMobile}
                    sx={{
                      whiteSpace: 'nowrap',
                      minWidth: isMobile ? undefined : ACTION_MIN_WIDTH,
                    }}
                  >
                    {isSaving ? 'Saving…' : 'Save'}
                  </Button>
                </Box>
              </Tooltip>
            </Stack>

            <Divider />

            <DangerZoneCard
              priority="danger"
              title="Delete Stock Code"
              description={
                <>
                  Deleting will mark this Stock Code as deleted. This action can’t be easily undone.
                  <br />
                  <b>{originalStockCode || form.stockCode}</b>
                </>
              }
              actions={
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDelete}
                  disabled={isBusy}
                  startIcon={isDeleting ? <CircularProgress size={18} /> : <DeleteOutlineIcon />}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Delete Stock Code
                </Button>
              }
            />
          </Stack>
        </Paper>
      </Stack>
    </Box>
  );
};

export default EditStockCode;
